"use client";

import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslations } from "next-intl";
import { Badge } from "@/components/shadcn/badge";
import { Button } from "@/components/shadcn/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
} from "@/components/shadcn/input-group";
import { Kbd } from "@/components/shadcn/kbd";
import { cn } from "@/lib/utils";
import { CheckIcon } from "lucide-react";
import { useComposerKeys } from "../composerKeys";
import type { ActiveQuestions } from "./useQuestions";
import {
  MAX_CUSTOM_ANSWER_LENGTH,
  type Question,
  type SubmittedAnswer,
} from "./questionTypes";

type Draft = { selected: string[]; custom: string; otherOpen: boolean };

const EMPTY_DRAFT: Draft = { selected: [], custom: "", otherOpen: false };

function isAnswered(draft: Draft): boolean {
  return draft.otherOpen
    ? draft.custom.trim() !== ""
    : draft.selected.length > 0;
}

function toAnswer(question: Question, draft: Draft): SubmittedAnswer {
  // A custom answer replaces the options, as the backend expects.
  return draft.otherOpen
    ? { header: question.header, custom_answer: draft.custom.trim() }
    : { header: question.header, selected: draft.selected };
}

type QuestionComposerProps = {
  active: ActiveQuestions;
  onSendDifferentMessage: () => void;
};

// Each call is its own card: its position and drafts never carry over to the
// next call, which may have fewer questions.
export function QuestionComposer(props: QuestionComposerProps) {
  return <QuestionCard key={props.active.call.toolCallId} {...props} />;
}

function QuestionCard({
  active,
  onSendDifferentMessage,
}: QuestionComposerProps) {
  const t = useTranslations("widget.questions");
  const { call, remaining } = active;
  const { questions } = call;
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [highlighted, setHighlighted] = useState(0);
  const [drafts, setDrafts] = useState<Draft[]>(() =>
    questions.map(() => EMPTY_DRAFT)
  );
  const [submitting, setSubmitting] = useState(false);

  const question = questions[index];
  const draft = drafts[index] ?? EMPTY_DRAFT;
  const otherIndex = question.options.length;
  const isLast = index === questions.length - 1;

  useEffect(() => {
    if (!draft.otherOpen) containerRef.current?.focus({ preventScroll: true });
  }, [draft.otherOpen, index, call.toolCallId]);

  // The options scroll on a short screen: keep the keyboard's row in view.
  useEffect(() => {
    optionsRef.current
      ?.querySelector("[data-highlighted]")
      ?.scrollIntoView({ block: "nearest" });
  }, [highlighted, index]);

  const update = (next: Partial<Draft>): void =>
    setDrafts((all) =>
      all.map((d, i) => (i === index ? { ...d, ...next } : d))
    );

  const submit = async (answered: Draft[]): Promise<void> => {
    if (!answered.every(isAnswered) || submitting) return;
    setSubmitting(true);
    try {
      await active.answer({
        answers: questions.map((q, i) => toAnswer(q, answered[i])),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const skip = async (): Promise<void> => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await active.answer({ cancelled: true });
    } finally {
      setSubmitting(false);
    }
  };

  const goTo = (next: number): void => {
    setIndex(next);
    setHighlighted(0);
  };

  // Moving past the last question sends the answers: there is no submit step.
  const advance = (answered: Draft[] = drafts): void => {
    if (!isAnswered(answered[index])) return;
    if (isLast) void submit(answered);
    else goTo(index + 1);
  };

  const pick = (row: number): void => {
    setHighlighted(row);
    if (row === otherIndex) {
      update({ otherOpen: true, selected: [] });
      return;
    }
    const label = question.options[row].label;
    if (question.multiSelect) {
      update({
        otherOpen: false,
        selected: draft.selected.includes(label)
          ? draft.selected.filter((l) => l !== label)
          : [...draft.selected, label],
      });
    } else {
      // A single-choice row is the answer: picking it moves on.
      const answered = drafts.map((d, i) =>
        i === index ? { ...d, otherOpen: false, selected: [label] } : d
      );
      setDrafts(answered);
      advance(answered);
    }
  };

  const rowCount = question.options.length + 1;

  const handleKey = (e: KeyboardEvent | ReactKeyboardEvent): boolean => {
    if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return false;
    if ((e.key === "Enter" || e.key === " ") && isFooterButton(e.target))
      return false;
    const digit = Number.parseInt(e.key, 10);
    if (String(digit) === e.key && digit >= 1 && digit <= rowCount) {
      pick(digit - 1);
      return true;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      const step = e.key === "ArrowDown" ? 1 : -1;
      setHighlighted((i) => (i + step + rowCount) % rowCount);
      return true;
    }
    if (e.key === " ") {
      pick(highlighted);
      return true;
    }
    if (e.key === "Enter") {
      if (isAnswered(draft)) advance();
      else if (!question.multiSelect || highlighted === otherIndex)
        pick(highlighted);
      return true;
    }
    if (e.key === "ArrowLeft" && index > 0) {
      goTo(index - 1);
      return true;
    }
    if (e.key === "ArrowRight" && !isLast && isAnswered(draft)) {
      goTo(index + 1);
      return true;
    }
    return false;
  };

  useComposerKeys({
    containerRef,
    listening: !draft.otherOpen && !submitting,
    onKey: handleKey,
    isChoiceKey: (key) => {
      const digit = Number.parseInt(key, 10);
      return String(digit) === key && digit >= 1 && digit <= rowCount;
    },
  });

  const onOtherKey = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      update({ otherOpen: false });
    } else if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      advance();
    }
  };

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      data-question-card
      data-composer-card
      aria-label={t("cardLabel")}
      className="outline-none rounded-xl border border-border bg-card shadow-sm overflow-hidden"
    >
      <div className="flex items-start gap-2 px-4 pt-3 pb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{question.header}</Badge>
            {questions.length > 1 && (
              <span className="text-xs text-muted-foreground">
                {t("progress", { index: index + 1, total: questions.length })}
              </span>
            )}
            {remaining > 0 && (
              <span className="text-xs text-muted-foreground">
                {t("moreToCome", { count: remaining })}
              </span>
            )}
          </div>
          <p className="mt-1.5 text-sm font-medium text-foreground whitespace-pre-line">
            {question.question}
          </p>
          {question.multiSelect && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("pickAll")}
            </p>
          )}
        </div>
      </div>

      <div
        role={question.multiSelect ? "group" : "radiogroup"}
        aria-label={question.question}
        ref={optionsRef}
        className="flex max-h-[45vh] flex-col gap-0.5 overflow-y-auto border-t border-border p-1.5"
      >
        {question.options.map((option, row) => {
          const chosen =
            !draft.otherOpen && draft.selected.includes(option.label);
          return (
            <OptionRow
              key={option.label}
              index={row}
              highlighted={highlighted === row}
              chosen={chosen}
              multiSelect={question.multiSelect}
              ariaLabel={option.label}
              onHover={() => setHighlighted(row)}
              onSelect={() => pick(row)}
            >
              <span className="flex min-w-0 flex-col">
                <span className="font-medium">{option.label}</span>
                {option.description && (
                  <span className="text-xs text-muted-foreground">
                    {option.description}
                  </span>
                )}
              </span>
            </OptionRow>
          );
        })}
        {draft.otherOpen ? (
          <div className="flex items-start gap-2 rounded-lg bg-muted px-2.5 py-2">
            <OptionNumber index={otherIndex} className="mt-2.5" />
            {question.multiSelect && <CheckMark checked className="mt-3" />}
            <InputGroup className="min-w-0 flex-1 bg-card">
              <InputGroupTextarea
                autoFocus
                aria-label={t("ownAnswer")}
                value={draft.custom}
                maxLength={MAX_CUSTOM_ANSWER_LENGTH}
                onChange={(e) => update({ custom: e.target.value })}
                onKeyDown={onOtherKey}
                placeholder={t("ownAnswerPlaceholder")}
              />
              <InputGroupAddon align="block-end">
                <InputGroupButton
                  size="sm"
                  className="-ml-2.5 rounded-lg"
                  aria-label={t("backToOptions")}
                  onClick={() => update({ otherOpen: false })}
                >
                  {t("options")} <Kbd>Esc</Kbd>
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
          </div>
        ) : (
          <OptionRow
            index={otherIndex}
            highlighted={highlighted === otherIndex}
            chosen={false}
            multiSelect={question.multiSelect}
            ariaLabel={t("other")}
            onHover={() => setHighlighted(otherIndex)}
            onSelect={() => pick(otherIndex)}
          >
            <span className="font-medium">{t("other")}</span>
            <span className="text-muted-foreground truncate">
              {t("otherHint")}
            </span>
          </OptionRow>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-1.5 py-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          disabled={submitting}
          onClick={onSendDifferentMessage}
        >
          {t("sendDifferent")}
        </Button>
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            disabled={submitting}
            onClick={() => void skip()}
          >
            {t("skipAll")}
          </Button>
          {index > 0 && (
            <Button
              variant="outline"
              size="sm"
              disabled={submitting}
              onClick={() => goTo(index - 1)}
            >
              {t("back")}
            </Button>
          )}
          {question.multiSelect && (
            <Button
              size="sm"
              disabled={!isAnswered(draft) || submitting}
              onClick={() => advance()}
            >
              {t("continue")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function OptionRow({
  index,
  highlighted,
  chosen,
  multiSelect,
  ariaLabel,
  onHover,
  onSelect,
  children,
}: {
  index: number;
  highlighted: boolean;
  chosen: boolean;
  multiSelect: boolean;
  ariaLabel: string;
  onHover: () => void;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role={multiSelect ? "checkbox" : "radio"}
      aria-checked={chosen}
      aria-label={ariaLabel}
      aria-keyshortcuts={String(index + 1)}
      data-highlighted={highlighted || undefined}
      onMouseEnter={onHover}
      onFocus={onHover}
      onClick={onSelect}
      className={cn(
        "flex w-full min-w-0 items-start gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-foreground outline-none transition-[color,background-color,box-shadow] focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        highlighted && "bg-muted",
        chosen && !multiSelect && "bg-muted ring-1 ring-border"
      )}
    >
      <OptionNumber index={index} />
      {multiSelect && <CheckMark checked={chosen} />}
      {children}
    </button>
  );
}

// Plain text, not a key cap: its digit lines up with the question above it,
// and a checkbox beside it reads as one row, as in Claude Code's question list.
function OptionNumber({
  index,
  className,
}: {
  index: number;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "w-4 shrink-0 text-muted-foreground tabular-nums",
        className
      )}
    >
      {index + 1}.
    </span>
  );
}

// Drawn, not the shadcn Checkbox: that one is a button, and a button inside an
// option row's button is invalid HTML. The row is the checkbox.
function CheckMark({
  checked,
  className,
}: {
  checked: boolean;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      data-state={checked ? "checked" : "unchecked"}
      className={cn(
        "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input data-[state=checked]:border-primary data-[state=checked]:bg-primary",
        className
      )}
    >
      {checked && <CheckIcon className="size-3" color="var(--background)" />}
    </span>
  );
}

// Skip all, Back and the rest act on Enter and Space themselves; only option
// rows hand those keys to the card.
function isFooterButton(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    target.closest("button") !== null &&
    target.closest('[role="radio"],[role="checkbox"]') === null
  );
}
